import ExcelJS from "exceljs";
import { ApolloClient } from "@apollo/client";
import { endOfDay, startOfDay } from "date-fns";
import { GETPROJECTACTIVITY, formatRange, toDays } from "./types";
import { addSummarySheet, addTableSheet, buildSummaryExport, renderChartImage, saveWorkbook } from "./exportSummary";
import { GETPROJECTCALLLOG, ICallLogRow, getCallColumns } from "./CallsDetails";
import { countPrimaryObjections } from "./objections";

// Project report workbook for the User tab's date range:
// 1. the User dashboard (metric table, chart, daily table), 2. the Details calls table, 3. Primary Objection counts
export const downloadProjectReport = async (client: ApolloClient<any>, project: { _id: string; name: string }, dateStart: Date, dateEnd: Date) => {
  const start = startOfDay(dateStart);
  const end = endOfDay(dateEnd);
  const range = formatRange(start, end);
  const variables = { project: project._id, dateStart: start, dateEnd: end };

  const [{ data: statsData }, { data: callData }] = await Promise.all([
    client.query({ query: GETPROJECTACTIVITY, fetchPolicy: "network-only", variables }),
    client.query({ query: GETPROJECTCALLLOG, fetchPolicy: "network-only", variables }),
  ]);

  const workbook = new ExcelJS.Workbook();

  // 1. User dashboard
  const days = toDays(statsData.projectActivityStats, start, end);
  addSummarySheet(workbook, buildSummaryExport(project.name, range, days, renderChartImage(days)), "User Dashboard");

  // 2. Outbound calls, same columns and values as the Details table
  const calls: ICallLogRow[] = callData.projectCallLog || [];
  const columns = getCallColumns(project._id, start, end);
  addTableSheet(
    workbook,
    "Outbound Calls",
    columns.map((column) => column.Header),
    calls.map((call) =>
      columns.map((column) => {
        const value = column.accessor(call);
        return String(column.exportValue ? column.exportValue(value) : value);
      })
    ),
    columns.map((column) => (column.Header === "Note" ? 60 : column.Header === "Date" ? 22 : 24))
  );

  // 3. Primary Objection counts over the same calls (the Details table's Primary Objection column)
  addTableSheet(
    workbook,
    "Primary Objections",
    ["Primary Objection", "Count"],
    [...countPrimaryObjections(calls, start, end), ["Total", calls.length]],
    [40, 12]
  );

  await saveWorkbook(workbook, `${project.name} Project Report ${range}.xlsx`);
};
